/**
 * Configuration partagée admin.
 *
 * ⚠️ DEUX SOURCES, à ne pas confondre :
 *
 *   • LA TAXONOMIE (domaine → compétence → notion) vient du BACKEND, via
 *     `taxonomyClient` : c'est la donnée que l'admin édite, elle ne peut pas
 *     être figée dans une copie de fichier régénérée au build. Le client
 *     replie sur `_shared_base` tant qu'il n'est pas hydraté (App.jsx).
 *
 *   • LE RESTE (types de jeu, difficultés, cycles, thèmes…) reste porté par
 *     `_shared_base.js`, copie de la source frontend synchronisée au build.
 *     Ce n'est pas de la donnée éditable, il n'y a rien à servir.
 *
 * Chantier « ID de compétence », phase P3.
 * Documentation/IMPLEMENTATION/CARTOGRAPHIE_IMPACT_ID_COMPETENCES.md
 */
import shared from './_shared_base.js';
import * as taxonomy from './taxonomyClient.js';

// ── Ne relève pas de la taxonomie : copie embarquée ──
export const {
  GAME_TYPES,
  GAME_TYPE_IDS,
  GESTURE_GAME_TYPE_IDS,
  DIFFICULTIES,
  getGameConfig,
  getGamesList,
  normalizeGameTypeId,
  isGameTypeId,
  getGameTypeLabel,
  cycleToSchoolLevel,
  cycleCeilingLevel,
  cyclePositionLevel,
  cyclePositionIndex,
  ordreNiveaux,
  SCENARIO_THEMES,
  getScenarioThemes,
  SCENARIO_TEMPOS,
  getScenarioTempos,
} = shared;

// ── Taxonomie : servie par le backend ──
export const {
  getAllCompetences,
  getAllAvailableNotions,
  getCompetencesforLevel,
  getCompetenceName,
  getNotionLabel,
  isAssociationFitForCompetence,
  getAssociationFitAssessment,
  getGameCompetences,
  getThemes,
  getLessons,
  hydraterTaxonomie,
  rafraichirTaxonomie,
  getTaxonomySource,
} = taxonomy;

/**
 * `DOMAIN_NAMES` était un objet importé directement par 7 écrans. Il devient un
 * getter : sa valeur dépend maintenant de l'hydratation, et un objet figé à
 * l'import aurait gardé les libellés de la copie embarquée pour toute la
 * session — sans que rien ne le signale.
 *
 * Les appelants écrivent `DOMAIN_NAMES[domaine]` : un objet-proxy préserve cette
 * écriture tout en lisant la source courante à chaque accès.
 */
export const DOMAIN_NAMES = new Proxy(
  {},
  {
    get: (_, propriete) => taxonomy.getDomainNames()[propriete],
    has: (_, propriete) => propriete in taxonomy.getDomainNames(),
    ownKeys: () => Reflect.ownKeys(taxonomy.getDomainNames()),
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
  }
);

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  ADMIN: {
    HOME: '/admin',
    EXERCISES: {
      LIST: '/admin/exercises',
      CREATE: '/admin/exercises/create',
      STUDIO: '/admin/exercises/studio',
      EDIT: id => `/admin/exercises/edit/${id}`,
      VIEW: id => `/admin/exercises/view/${id}`,
    },
    USERS: '/admin/users',
    STATS: '/admin/stats',
    PROMPT_ENGINEERING: '/admin/prompt-engineering',
    MARKETING: '/admin/marketing',
    PRODUCTS_AUDIT: '/admin/products-audit',
    PARENT_FEEDBACKS: '/admin/parent-feedbacks',
  },
};
